import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, X } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { formatErr, inputCls } from '../billing/shared';

const useProblems = () => {
  const { authHeader } = useAuth();
  const [items, setItems] = useState(null);
  useEffect(() => {
    axios.get(`${API}/appointments/common-problems`, { headers: authHeader() })
      .then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  }, []); // eslint-disable-line
  return [items, setItems];
};

const hasLine = (notes, p) => (notes || '').split('\n').some((l) => l.trim().toLowerCase() === p.toLowerCase());

// Tap a common problem to add it to (or remove it from) the remarks.
export const ProblemPicker = ({ value, onChange }) => {
  const [items] = useProblems();
  if (!items?.length) return null;
  const toggle = (p) => {
    const lines = (value || '').split('\n');
    onChange(hasLine(value, p)
      ? lines.filter((l) => l.trim().toLowerCase() !== p.toLowerCase()).join('\n')
      : [...lines.filter((l) => l.trim()), p].join('\n'));
  };
  return (
    <div className="flex flex-wrap gap-1.5 mb-2" data-testid="apt-problem-picker">
      {items.map((p) => {
        const on = hasLine(value, p);
        return (
          <button key={p} type="button" onClick={() => toggle(p)} aria-pressed={on}
            className={`text-xs rounded-full border px-2.5 py-1 transition-colors ${on ? 'border-teal-600 bg-teal-600 text-white' : 'border-slate-300 text-slate-600 hover:border-teal-500'}`}
            data-testid={`apt-problem-${p.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{p}</button>
        );
      })}
    </div>
  );
};

export const CommonProblemsEditor = () => {
  const { authHeader } = useAuth();
  const [items, setItems] = useProblems();
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false); const [msg, setMsg] = useState(''); const [err, setErr] = useState('');
  const add = () => { const v = draft.trim(); if (v && !items.some((i) => i.toLowerCase() === v.toLowerCase())) setItems([...items, v]); setDraft(''); };
  const save = async () => {
    setBusy(true); setMsg(''); setErr('');
    try { const { data } = await axios.put(`${API}/appointments/common-problems`, { items }, { headers: authHeader() }); setItems(data.items); setMsg('Saved.'); }
    catch (e) { setErr(formatErr(e)); } finally { setBusy(false); }
  };
  if (!items) return null;
  return (
    <section className="bg-white border border-slate-200 rounded-xl p-5 space-y-3" data-testid="common-problems-section">
      <div>
        <h2 className="text-sm font-semibold text-teal-700 uppercase tracking-wider">Common problems</h2>
        <p className="text-xs text-slate-500 mt-1">Shown as quick picks under Problem / remarks when booking an appointment.</p>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((p) => (
          <span key={p} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-700" data-testid="common-problem-chip">
            {p}<button type="button" onClick={() => setItems(items.filter((x) => x !== p))} className="text-slate-400 hover:text-red-600" aria-label={`Remove ${p}`}><X size={12} /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} placeholder="Add a problem, e.g. Horn not working" className={`${inputCls} max-w-xs`} data-testid="common-problem-input" />
        <button type="button" onClick={add} className="text-sm font-semibold border border-slate-300 hover:border-teal-500 rounded-lg px-3" data-testid="common-problem-add">Add</button>
        <button type="button" onClick={save} disabled={busy} className="inline-flex items-center gap-1.5 bg-teal-500 hover:bg-teal-600 disabled:bg-teal-300 text-white text-sm font-semibold px-4 py-2 rounded-lg" data-testid="common-problems-save">
          {busy && <Loader2 size={14} className="animate-spin" />} Save
        </button>
      </div>
      {msg && <p className="text-xs text-emerald-700" data-testid="common-problems-ok">{msg}</p>}
      {err && <p className="text-xs text-red-700" data-testid="common-problems-error">{err}</p>}
    </section>
  );
};
